/**
 * Utilidades para manejo de fechas con zona horaria de Perú
 */

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD para la zona horaria de Perú (America/Lima)
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const getPeruDateString = () => {
  const today = new Date();
  const peruDate = new Date(today.toLocaleString("en-US", { timeZone: "America/Lima" }));
  return peruDate.getFullYear() + '-' + 
         String(peruDate.getMonth() + 1).padStart(2, '0') + '-' + 
         String(peruDate.getDate()).padStart(2, '0');
};

/**
 * Obtiene la fecha y hora actual en zona horaria de Perú
 * @returns {Date} Objeto Date ajustado a zona horaria de Perú
 */
export const getPeruDate = () => {
  const today = new Date();
  return new Date(today.toLocaleString("en-US", { timeZone: "America/Lima" }));
};

/**
 * Convierte una fecha a string en zona horaria de Perú
 * @param {Date} date - Fecha a convertir
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const formatDateForPeru = (date) => {
  const peruDate = new Date(date.toLocaleString("en-US", { timeZone: "America/Lima" }));
  return peruDate.getFullYear() + '-' + 
         String(peruDate.getMonth() + 1).padStart(2, '0') + '-' + 
         String(peruDate.getDate()).padStart(2, '0');
};

/**
 * Obtiene la fecha actual en formato legible para Perú
 * @returns {string} Fecha en formato DD/MM/YYYY
 */
export const getPeruDateDisplay = () => {
  const peruDate = getPeruDate();
  return String(peruDate.getDate()).padStart(2, '0') + '/' + 
         String(peruDate.getMonth() + 1).padStart(2, '0') + '/' + 
         peruDate.getFullYear();
};

/**
 * Obtiene la fecha y hora actual formateada para Perú
 * @returns {string} Fecha y hora en formato DD/MM/YYYY HH:mm:ss
 */
export const getPeruDateTimeDisplay = () => {
  const peruDate = getPeruDate();
  const dateStr = String(peruDate.getDate()).padStart(2, '0') + '/' + 
                  String(peruDate.getMonth() + 1).padStart(2, '0') + '/' + 
                  peruDate.getFullYear();
  const timeStr = String(peruDate.getHours()).padStart(2, '0') + ':' + 
                  String(peruDate.getMinutes()).padStart(2, '0') + ':' + 
                  String(peruDate.getSeconds()).padStart(2, '0');
  return dateStr + ' ' + timeStr;
};

/**
 * Fecha y hora actual en Perú para enviar al API (YYYY-MM-DD HH:mm:ss).
 * El backend también normaliza al crear turno detalle; esto evita UTC por toISOString().
 */
export const getPeruDateTimeSqlString = () => {
  const d = getPeruDate();
  return (
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
  );
};

/**
 * Clave numérica YYYYMMDD para ordenar fecha_turno sin depender de new Date()
 * (evita problemas de zona horaria y formatos mixtos ISO vs DD/MM/YYYY).
 */
export const fechaTurnoToSortKey = (fecha) => {
  if (fecha == null || fecha === '') return 0;
  const s = String(fecha).trim();
  const datePart = s.split('T')[0].split(' ')[0];

  const iso = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return (
      parseInt(iso[1], 10) * 10000 +
      parseInt(iso[2], 10) * 100 +
      parseInt(iso[3], 10)
    );
  }

  const dmy = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return (
      parseInt(dmy[3], 10) * 10000 +
      parseInt(dmy[2], 10) * 100 +
      parseInt(dmy[1], 10)
    );
  }

  return 0;
};

/** Timestamp para ordenar created_at / updated_at */
export const dateTimeToSortKey = (value) => {
  if (value == null || value === '') return 0;
  const normalized = String(value).trim().replace(' ', 'T');
  const t = new Date(normalized).getTime();
  return Number.isNaN(t) ? 0 : t;
};

export default {
  getPeruDateString,
  getPeruDate,
  formatDateForPeru,
  getPeruDateDisplay,
  getPeruDateTimeDisplay,
  getPeruDateTimeSqlString,
  fechaTurnoToSortKey,
  dateTimeToSortKey,
};